import { useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  Users, ArrowRight, Sparkles,
  Clock, Star, Shield, CheckCircle,
  BookOpen, Video, Calendar, TrendingUp, Target, BarChart2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { cn } from "@/lib/utils";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { OFFERING_TYPE_THEME } from "@/lib/offeringTypeTheme";

/**
 * Copy + decorative highlight icons per type; identity colour + main icon come
 * from the shared offering-type theme so this page matches search cards, the
 * wizard, sidebar, and dashboard rails.
 */
const offeringTypes = [
  {
    value: "one_to_one",
    label: "1-on-1 Teaching",
    tagline: "Personalized, private sessions",
    description:
      "Work directly with one student at a time. Tailored curriculum, flexible pacing, and undivided attention for maximum learning outcomes.",
    icon: OFFERING_TYPE_THEME.ONE_TO_ONE.icon,
    accentColor: OFFERING_TYPE_THEME.ONE_TO_ONE.accent,
    accentBg: "bg-emerald-500/8",
    accentBorder: "border-emerald-400/20",
    hoverBorder: "hover:border-emerald-400/50",
    gradientFrom: "from-emerald-100/40",
    gradientTo: "to-amber-50/20",
    highlights: [
      { icon: Clock,    text: "Flexible scheduling" },
      { icon: BookOpen, text: "Custom curriculum" },
      { icon: Star,     text: "Focused attention" },
    ],
    badge: "Most Popular",
    badgeColor: "bg-emerald-100 text-emerald-700",
  },
  {
    value: "group",
    label: "Group Classes",
    tagline: "Collaborative cohort sessions",
    description:
      "Teach multiple students together in live interactive sessions. Build community, encourage peer learning, and scale your reach.",
    icon: OFFERING_TYPE_THEME.GROUP.icon,
    accentColor: OFFERING_TYPE_THEME.GROUP.accent,
    accentBg: "bg-violet-500/8",
    accentBorder: "border-violet-400/20",
    hoverBorder: "hover:border-violet-400/50",
    gradientFrom: "from-violet-100/40",
    gradientTo: "to-purple-50/20",
    highlights: [
      { icon: Users,     text: "Up to 20 students" },
      { icon: Video,     text: "Live sessions" },
      { icon: Calendar,  text: "Scheduled cohorts" },
    ],
    badge: null,
    badgeColor: "",
  },
  {
    value: "crash_course",
    label: "Crash Course",
    tagline: "Intensive time-bound program",
    description:
      "Run a structured, deadline-driven course for exam prep or skill sprints. Fixed dates, weekly plan, and results-focused content.",
    icon: OFFERING_TYPE_THEME.CRASH_COURSE.icon,
    accentColor: OFFERING_TYPE_THEME.CRASH_COURSE.accent,
    accentBg: "bg-amber-500/8",
    accentBorder: "border-amber-400/20",
    hoverBorder: "hover:border-amber-400/50",
    gradientFrom: "from-amber-100/40",
    gradientTo: "to-yellow-50/20",
    highlights: [
      { icon: Target,   text: "Exam / goal focused" },
      { icon: BarChart2, text: "Week-by-week plan" },
      { icon: Calendar, text: "Fixed dates & seats" },
    ],
    badge: null,
    badgeColor: "",
  },
];


export default function CreateOfferingPage() {
  usePageTitle('Create Offering | BookMyCoaching');
  const navigate = useNavigate();
  const { teacherProfile } = useTeacherProfile();
  const teacherName = teacherProfile?.fullName || "";
  const teacherAvatar = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || []).map((s: any) =>
    typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || ""
  ).filter(Boolean);

  const location = useLocation();
  const getStoredClone = () => {
    // Prefer direct navigation state
    const stateClone = (location.state as any)?.cloneFrom;
    if (stateClone) return stateClone;
    // Only fallback to sessionStorage if the navigation explicitly came from a Clone action
    if (!(location.state as any)?.fromClone) return undefined;
    if (typeof window === 'undefined') return undefined;
    try {
      const raw = sessionStorage.getItem('cloneFromOffering');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      // remove after reading so it doesn't affect other flows
      try { sessionStorage.removeItem('cloneFromOffering'); } catch { /* ignore */ }
      return parsed;
    } catch (e) { return undefined; }
  };

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />

          <div className="flex-1 flex flex-col min-w-0">
            <main className="flex-1 overflow-auto bg-slate-50">

              <div className="max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">

                {/* Page header card */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex flex-col sm:flex-row">
                    {/* Left — gradient zone */}
                    <div className="relative flex-1 px-6 py-6 bg-[hsl(200,55%,95%)] overflow-hidden">
                      <div className="absolute -top-6 -left-6 w-32 h-32 bg-[hsl(210,60%,28%)]/5 rounded-full blur-2xl pointer-events-none" />
                      <div className="relative z-10">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Sparkles className="w-3.5 h-3.5 text-[hsl(210,60%,28%)]" />
                          <span className="text-xs font-bold uppercase tracking-widest text-[hsl(210,60%,28%)]">New Offering</span>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-800">What would you like to create?</h1>
                        <p className="text-sm text-slate-500 mt-1">Choose a format below. Each has a tailored setup wizard to help you go live quickly.</p>
                      </div>
                    </div>
                    {/* Right — highlights */}
                    <div className="flex flex-col justify-center gap-3 px-6 py-6 border-t sm:border-t-0 sm:border-l border-slate-100 shrink-0 min-w-[180px]">
                      {[
                        { icon: Shield,     text: "Verified badge" },
                        { icon: TrendingUp, text: "Search visibility" },
                      ].map(({ icon: I, text }) => (
                        <div key={text} className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md bg-[hsl(210,60%,18%)]/8 flex items-center justify-center shrink-0">
                            <I className="w-3 h-3 text-[hsl(210,60%,28%)]" />
                          </div>
                          <span className="text-xs text-slate-500">{text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Offering type cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {offeringTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => {
                          const clone = getStoredClone();
                          if (clone) navigate(`/create-offering/${type.value}`, { state: { cloneFrom: clone } as any });
                          else navigate(`/create-offering/${type.value}`);
                        }}
                        className={cn(
                          "group relative text-left rounded-2xl border-2 border-slate-200 bg-white",
                          "transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden",
                          type.hoverBorder
                        )}
                      >
                        {/* Gradient fill top */}
                        <div className={cn(
                          "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br",
                          type.gradientFrom, type.gradientTo
                        )} />

                        {/* Popular badge */}
                        {type.badge && (
                          <span className={cn(
                            "absolute top-4 right-4 text-xs font-bold px-2 py-0.5 rounded-full",
                            type.badgeColor
                          )}>
                            {type.badge}
                          </span>
                        )}

                        <div className="relative z-10 p-7">
                          {/* Icon */}
                          <div className={cn(
                            "w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-300",
                            type.accentBg,
                            "transition-colors"
                          )}>
                            <Icon className={cn("w-6 h-6", type.accentColor)} />
                          </div>

                          {/* Title */}
                          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">{type.tagline}</p>
                          <h3 className={cn("text-xl font-bold text-slate-800 mb-3 transition-colors group-hover:", type.accentColor)}>
                            {type.label}
                          </h3>
                          <p className="text-sm text-slate-500 leading-relaxed mb-6">
                            {type.description}
                          </p>

                          {/* Feature highlights */}
                          <div className="space-y-2 mb-7">
                            {type.highlights.map(({ icon: HIcon, text }) => (
                              <div key={text} className="flex items-center gap-2.5">
                                <div className={cn("w-5 h-5 rounded-md flex items-center justify-center shrink-0", type.accentBg)}>
                                  <HIcon className={cn("w-3 h-3", type.accentColor)} />
                                </div>
                                <span className="text-xs text-slate-600">{text}</span>
                              </div>
                            ))}
                          </div>

                          {/* CTA row */}
                          <div className={cn(
                            "inline-flex items-center gap-2 text-sm font-semibold transition-all duration-200",
                            type.accentColor,
                            "group-hover:gap-3"
                          )}>
                            Get started
                            <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>


                {/* Tips card */}
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-6 py-5 flex items-start gap-4">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800 mb-1">Tips for a great listing</p>
                    <ul className="text-xs text-emerald-700 space-y-1 leading-relaxed">
                      <li>• Name your offering around the outcome — e.g. "IELTS Band 7+ in 8 weeks" beats "English Teaching".</li>
                      <li>• Be specific about session duration, frequency, and what's covered so students know exactly what they're signing up for.</li>
                      <li>• Enable a free trial session — it significantly increases first enquiries and builds immediate trust.</li>
                      <li>• Price based on your preparation time, not just session time — materials, feedback, and planning all count.</li>
                    </ul>
                  </div>
                </div>

              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
